// src/components/chart/ReasonForVisit.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import config from '../../config';
import { postData, localData, usDate, notify, Age, Gender} from '../../functions';
import Select from 'react-select';
import Header from '../common/Header';
import Footer from '../common/Footer';
import { PulseLoader } from 'react-spinners';

const ReasonForVisit = () => {  
  const [isChange, setIsChange] = useState(false);
  const [reasonForVisitData, setReasonForVisitData] = useState(localData('reason_for_visit_data'));
  const [DBvtOption, setVtOptionVal] = useState(null);
  const [isReferralYes, setIsReferralYes] = useState(null);
  const [loadDefaultData, setLoadDefaultData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [defaultFetched, setdefaultFetched] = useState(false);
  const [vtOptions, setVtOptions] = useState([]);
  const [rfvOptions, setRfvOptions] = useState([]);
  const [patientData, setPatientData] = useState(localData('patient_data'));
  const goto = useNavigate();
  const chartStatus = localData('chart_status');
  const [openAccordions, setOpenAccordions] = useState([]);

  const [firstLoad, setFirstLoad] = useState(false);



  const initialOptions = localData('reason_for_visit_options_response');
  const reasonForVisitOptions = initialOptions ? initialOptions : [];

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const resultsRef = useRef(null);

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);
    const words = value.split(' ').filter(Boolean);
    const results = reasonForVisitOptions.filter((item) =>
      words.every((word) => item.rfv_name.toLowerCase().includes(word))
    );
    setSearchResults(results);
    setShowResults(value !== '' && results.length > 0);
  };


  const handleAccordion = (index) => {
      if (openAccordions.includes(index)) {
      setOpenAccordions(openAccordions.filter((item) => item !== index));
      } else {
      setOpenAccordions([...openAccordions, index]);
      }
  };

  const [reasonForVisitForm, setReasonForVisitForm] = useState({
    vt_ids: (reasonForVisitData && reasonForVisitData.vt_ids) || {},   
    rfv_ids: (reasonForVisitData && reasonForVisitData.rfv_ids) || '',
    rfvData: (reasonForVisitData && reasonForVisitData.rfvData) || {},
    referral: (reasonForVisitData && reasonForVisitData.referral) || ''
  });

  const updatePatient = async (patient_data) => {
    const result = await postData(`${config.chartServerUrl}/store/patient`, true, { chart_id: localData("chart_id"), patient_data: patient_data });
    return result;
  }

  const updateReasonForVisit = async ( vt_id, reason_for_visit_data, raw_hpi) => {
    const result = await postData(`${config.chartServerUrl}/store/reason_for_visit`, true, { chart_id: localData("chart_id"), vt_id:vt_id, reason_for_visit_data: reason_for_visit_data, raw_hpi:raw_hpi });
    return result;
  }

  const getMedication = async (rfv_ids = []) => {
    const searchRfvIds = { "rfv_ids": rfv_ids };
    const result = await postData(`${config.apiServerUrl}/search/medication`, true, searchRfvIds);
    return result;
  };


  const getVisitType = async () => {
    const result = localData("visit_type");
    if (result) {      
      const data = result.map((items) => ({
          value: items.vt_id,
          label: items.vt_name,
          vt_hpi: items.vt_hpi,
      }));
      setVtOptions(data);
    }
  };


 

  const getAssessment = async (icdCodes = []) => {
    const query = { "icd_codes": icdCodes };
    const result = await postData(`${config.apiServerUrl}/search/assessment`, true, query);  
    const assessment_data = result.filter(item => item !== null);
    return assessment_data;
  };

  
  const makeCategory = (cats, rfv_id, defaultValues) => {
    const rosCatOptions = cats.reduce((acc, cat) => {
      const rfvId = rfv_id;
      const catsId = cat.cats_id;
      const catsLabel = cat.cats_name;
      const isMultiple = cat.cats_type === 'multiple';

      if (!acc[catsLabel]) {
        acc[catsLabel] = {
          id: catsId,
          rfvid: rfvId,
          label: catsLabel,
          options: [],
          isMultiple: isMultiple,
          defaultValue: defaultValues && defaultValues &&  defaultValues.ros_cats && defaultValues.ros_cats[catsLabel]
        };
      }

      const options = cat.cats_options.map(option => ({
        value: option.option_value,
        label: option.option_name,
      }));

      acc[catsLabel].options.push(...options);

      return acc;
    }, {});

    const dynamicSelectComponents = Object.values(rosCatOptions).map((cats, index) => (
      <div key={index}>
        <label  className="block text-sm font-medium mb-1">{cats.label}</label>
        <Select
          onChange={(selectedOption) => handleRfvRepeaterChange(cats.rfvid, 'ros_cats_'+cats.label, selectedOption)}
          key={index}
          name={`cats_${index}_ids`}
          options={cats.options}
          placeholder={`${cats.label}`}
          isSearchable={true}
          isMulti={cats.isMultiple}
          defaultValue={cats.defaultValue} 
          className="react-select-container"
          classNamePrefix="react-select"
        />
      </div>     
    ));

    return dynamicSelectComponents;
  };

  
  const vtHandleChange = (selectedOption) => {
    setIsChange(true);
    setVtOptionVal(selectedOption);
    reasonForVisitInput('vt_ids', { vt_id: selectedOption.value, vt_name: selectedOption.label, vt_hpi: selectedOption.vt_hpi });   
  };


  const rfvHandleChange = (selectedOption) => {
    setIsChange(true);
    reasonForVisitInput('rfv_ids', selectedOption);
    addRfvRepeater(selectedOption);
    setSearchResults([]);    
  };


  
  const rfvInputHandleChange = (key,value) => {
    setIsChange(true);
    reasonForVisitInput(key, value);
    if (key === 'referral') { 
      if (value === 'yes') {
        setIsReferralYes(true);
      } 
      if (value === 'no') {
        setIsReferralYes(false);
      }
    }
      
  };

  const reasonForVisitInput = (field, value) => {
    setReasonForVisitForm({
      ...reasonForVisitForm,
      [field]: value,
    });
  };

  const handleRfvRepeaterChange = (rfv_id, field, value) => {

    setIsChange(true);
    setReasonForVisitForm((prevRfvData) => ({
      ...prevRfvData,
      rfvData: {
        ...prevRfvData.rfvData,
        [rfv_id]: {
          ...prevRfvData.rfvData[rfv_id],
          [field]: value,
        },
      },
    }));
  };

 

  const addRfvRepeater = async (rfv_ids, default_category_selected_values = null) => {

   
    const existingRFVIds = localData('rfv_ids') || [];
    const isAlreadyAdded = existingRFVIds.some(id => id === rfv_ids);

    if (!isAlreadyAdded) {
      const updatedRFVIds = [...existingRFVIds, rfv_ids];
      localStorage.setItem('rfv_ids', JSON.stringify(updatedRFVIds));
    }

    const result = await postData(`${config.apiServerUrl}/search/rfv_category`, true, { "rfv_id": rfv_ids });    
      
    if (result) {  
      const getCategory = makeCategory(result, rfv_ids, default_category_selected_values);    
      setReasonForVisitForm((prevRfvData) => {
  
        let duration = '';
        let duration_type = '';
        let extra_notes = '';
        let timestamp = Date.now();
        if (default_category_selected_values) {
          let rfv = default_category_selected_values;
          duration = rfv.duration;
          duration_type = rfv.duration_type;
          extra_notes = rfv.extra_notes;       
          timestamp = rfv.timestamp;       
        }     
  
        let rfv_idsSelected = rfvOptions.find(option => option.value === rfv_ids);
        
        if (rfv_idsSelected) {
          const setRfv_id = rfv_idsSelected.value;
          return {
            ...prevRfvData,
            rfvData: {
              ...prevRfvData.rfvData,
              [setRfv_id]: { getDynamicCategoryOptions: getCategory, rfv_id: setRfv_id, rfv_name: rfv_idsSelected.label, duration, duration_type, extra_notes, timestamp: timestamp },
            },
          };
        } else if (prevRfvData.rfvData) {
          return {
            ...prevRfvData,
            rfvData: prevRfvData.rfvData,
          };
        } else {
          return {
            ...prevRfvData,
            rfvData: {},
          };
        }
  
      });  
    }
  };

  const removeRfvRepeater = (rfv_id) => {          
    setIsChange(true);
    setReasonForVisitForm((prevRfvData) => {
      const newrfvData = { ...prevRfvData.rfvData };
      delete newrfvData[rfv_id];
      return {
        ...prevRfvData,
        rfvData: newrfvData,
      };
    });

    const existingRFVIds = localData('rfv_ids') || [];
    const updatedRFVIds = existingRFVIds.filter(id => id.toString() !== rfv_id.toString());
    localStorage.setItem('rfv_ids', JSON.stringify(updatedRFVIds));

  };


  const filterReasonForVisitForm = (rfvData) => {
    const newRfvData = {};
    for (const key in rfvData) {
      if (Object.prototype.hasOwnProperty.call(rfvData, key)) {
        const item = rfvData[key];
        const newRfvItem = { ...item };
        delete newRfvItem.getDynamicCategoryOptions;
        const rosCats = {};
        for (const subKey in newRfvItem) {
          if (subKey.startsWith('ros_cats_')) {
            const category = subKey.substring('ros_cats_'.length);
            rosCats[category] = newRfvItem[subKey];
            delete newRfvItem[subKey];
          }
        }
        if (Object.keys(rosCats).length > 0) {
          newRfvItem.ros_cats = rosCats;
        }

        newRfvData[key] = newRfvItem;
      }
    }
    return newRfvData;
  };

  const getPatientData = useCallback(async () => {
    const result = await postData(`${config.patientServerUrl}/search/patient`, true, { patient_id: localData("patient_id") });
    if (result) {
      const patient_data = JSON.stringify(result);
      updatePatient(patient_data);
      localStorage.setItem('patient_data', patient_data);
      setPatientData(result);
    }
  }, []);

   const getReasonForVisit = async () => {    
    if (initialOptions) {
      const data = await initialOptions.map((items) => ({
          value: items.rfv_id,
          label: items.rfv_name,
      }));
      setRfvOptions(data);
    }
  };

  const getFirstLoad = useCallback(async () => {
    if (firstLoad) {
      getVisitType();
      localData(['rfv_ids'], true);
      getReasonForVisit();
      if (!patientData) {
        getPatientData();
      }
    }
    setFirstLoad(true); 
    
  }, [firstLoad, patientData, getPatientData]);


  useEffect(() => {
    if (chartStatus === 2) {
      goto('/waiting-room/reason/summary');   
    } else {
      if (!defaultFetched) {     
        setdefaultFetched(true);              
        if (!reasonForVisitData) { 
          setLoadDefaultData(true);
        }
          
      }
    }

    getFirstLoad();

    const handleClickOutside = (event) => {
      if (resultsRef.current && !resultsRef.current.contains(event.target)) {
        setSearchResults([]);
        setShowResults(true);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [goto, getFirstLoad, patientData, getPatientData, defaultFetched, reasonForVisitData, chartStatus]);
  
  
  const dynamicCategoryInitialized = () => {
    setTimeout(() => {
      const transformedData = [];  
      Object.values(reasonForVisitData.rfvData).forEach(item => {
          if (item.ros_cats) {
              Object.entries(item.ros_cats).forEach(([key, value]) => {
                  transformedData.push({
                      rfv_id: item.rfv_id,
                      name: `ros_cats_${key}`,
                      options: value
                  });
              });
          }
      });
  
      transformedData.forEach((item) => {
        setReasonForVisitForm((prevRfvData) => ({
          ...prevRfvData,
          rfvData: {
            ...prevRfvData.rfvData,
            [item.rfv_id]: {
              ...prevRfvData.rfvData[item.rfv_id],
              [item.name]: item.options,
            },
          },
        }));
      });
    }, 500);
  
  } 
  

  const handleDefaultData = async () => {     
    
    setLoadDefaultData(true);
    if (reasonForVisitData && !loadDefaultData) {
      
      const vtValues = vtOptions.find(option => option.value === (reasonForVisitData.vt_ids).vt_id);
      setVtOptionVal(vtValues);
 
      Object.values(reasonForVisitData.rfvData).forEach(item => {   
        addRfvRepeater(item.rfv_id, item);
      });

      if (reasonForVisitData.referral === 'yes') {
        setIsReferralYes(true);
      }else{
        setIsReferralYes(false);
      }

      dynamicCategoryInitialized();

    } 
  };
 
  if (!loadDefaultData) { 
    setTimeout(() => {
      handleDefaultData();
    }, 100);
  }



  const saveToDb = (hideMessage = false) => {
    const updatedReasonForVisitForm = filterReasonForVisitForm(reasonForVisitForm.rfvData);          
      const finalreasonForVisitForm = {
        ...reasonForVisitForm,
        rfvData: updatedReasonForVisitForm,
      }; 
    setReasonForVisitData(finalreasonForVisitForm);
    
    const reason_for_visit_data = JSON.stringify(finalreasonForVisitForm);
    localStorage.setItem('reason_for_visit_data', reason_for_visit_data);
    const patientInfoRawHpi = `${patientData ? Age(patientData.dob) : 'null'} old ${patientData ? Gender(patientData.sex) : 'null'} patient came for ${finalreasonForVisitForm.vt_ids.vt_hpi}.\nPatient has complaints of ${finalreasonForVisitForm && Object.values(finalreasonForVisitForm.rfvData).map(rfvItem => `${rfvItem.rfv_name} for ${rfvItem.duration} ${rfvItem.duration_type ? rfvItem.duration_type : 'Days'}`).join(', ')}. ${finalreasonForVisitForm && Object.values(finalreasonForVisitForm.rfvData).map((rfvItem, index) => (`${rfvItem.ros_cats ? (index === 0 ? 'Patient also mentioned that' : ' and') : ''} ${rfvItem.ros_cats ? rfvItem.rfv_name + " is" :""} ${rfvItem.ros_cats ? Object.entries(rfvItem.ros_cats).map(([categoryKey, category]) => (`${categoryKey} ${Array.isArray(category) ? category.map((item, index) => `${item.value}${index < category.length - 1 ? ', ' : ''}`).join('') : category.value}`)).join(', ') : ""}${rfvItem.ros_cats ? '.' :''}`)).join('')}`;
    localStorage.setItem('raw_hpi', patientInfoRawHpi);
    localStorage.setItem('gpt_assessment', null);
    localStorage.setItem('raw_hpi', patientInfoRawHpi);
    localStorage.setItem('gpt_assessment', null);

    if (isChange) { 
      localData(['custom_hpi'], true);
    }

    updateReasonForVisit(finalreasonForVisitForm.vt_ids.vt_id , reason_for_visit_data, patientInfoRawHpi);
    setIsChange(false);
    if (hideMessage !== true) {
      notify('success', 'Saved successfully.');
    }
  }

  const saveAssessmentMedication = async (medication_data, assessment_data) => {
    const result = await postData(`${config.chartServerUrl}/store/assessment_medication`, true, { chart_id: localData("chart_id"), "medication_data": medication_data, "assessment_data":assessment_data });
    return result;
  };


  const reasonForVisitFormSubmit = async (e) => {
    e.preventDefault();

    if (!loadDefaultData) {
      goto('/waiting-room/reason/summary')
    } else { 
      setIsSubmitting(true);
      try {
        const rfv_ids = Object.keys(reasonForVisitForm.rfvData).map(key => parseInt(key));
        if (rfv_ids.length === 0) {
          notify('error', 'Please select reason for visit!');
        } else { 
          const medication_result = await getMedication(rfv_ids);
          if (medication_result) { 
            const icdCodes = Object.values(medication_result).reduce((x, item) => {
              const icdData = item.icd_data?.map(icd => icd.icd_code) || [];
              return x.concat(icdData);
            }, []);       
            
            const assessment_result = await getAssessment(icdCodes);
            if (assessment_result) {          
              saveToDb(true);
              const s_medication_result = JSON.stringify(medication_result);
              const s_assessment_result = JSON.stringify(assessment_result);
              
              const save_assessment_medication = await saveAssessmentMedication(s_medication_result, s_assessment_result);
              if (save_assessment_medication) { 
                if (save_assessment_medication.status === 'success') {                
                  localStorage.setItem('medication_data', s_medication_result);
                  localStorage.setItem('assessment_data', s_assessment_result);
                  // notify('success', 'Chart analysis successfully.');
                  goto('/waiting-room/reason/summary');
                } else { 
                  notify('error', save_assessment_medication.message);
                }
              }

            }      
    
          }      
        }
      } catch (error) {
        notify('error', 'Reason for visit submission error!');
      } finally { 
        setIsSubmitting(false);
      }
    }

  };


  return (
    <>
      <Helmet>
        <title>Reason for Visit | { config.appName}</title>
        <meta name="description" content="Reason for Visit Page Description" />       
      </Helmet>

      <Header />

      <div className="container mx-auto mt-7 pb-28 px-2" >
        <div className="relative isolate">

          <div className="grid grid-cols-1 gap-4">
            <div>
              <ol className="flex items-center w-full text-sm font-medium text-center text-gray-500 dark:text-gray-400 sm:text-base">             
                <li className="flex md:w-full items-center text-indigo-600 dark:text-indigo-500 after:content-[''] after:w-full after:h-1 after:border-b after:border-gray-200 after:border-1 after:hidden sm:after:inline-block after:mx-6 xl:after:mx-10 dark:after:border-gray-700">
                  <Link to="/waiting-room/reason">
                    <span className="flex items-center after:content-['/'] sm:after:hidden after:mx-2 after:text-gray-200 dark:after:text-gray-500">
                      <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 me-2" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z"/>
                      </svg>
                      Reason <span className="hidden sm:inline-flex sm:ms-2"></span>
                    </span>
                  </Link>  
                </li>
                <li className="flex items-center  dark:after:border-gray-700">
                  <Link to="/waiting-room/reason/summary">
                  <span className="flex items-center  sm:after:hidden after:mx-2 after:text-gray-200 dark:after:text-gray-500">
                        <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 me-2" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"/>
                        </svg> 
                        Summary <span className="hidden sm:inline-flex sm:ms-2"></span>
                  </span>
                  </Link>
                </li>   
              </ol>
            </div>
            <div>
              <div className="space-y-10 divide-y divide-gray-900/10">
              
                <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:gap-y-8 pt-7 md:grid-cols-3">
                  <div className="px-4 sm:px-0">

                    <div className="overflow-hidden bg-white border border-gray-200 rounded-md shadow  dark:bg-gray-800 dark:border-gray-700 sm:rounded-lg mb-5">
                      <div className="px-4 py-3.5 sm:px-6 bg-indigo-500 dark:bg-gray-900">
                        <h3 className="text-base font-semibold leading-7 text-white flex items-center">
                          <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 me-2.5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z"/>
                          </svg>
                          <span>Patient Information</span>                        
                        </h3>
                      </div>
                      <div className="border-t border-gray-100 dark:border-gray-700">
                        <dl className="divide-y divide-gray-100 dark:divide-gray-700"> 

                          <div className="px-4 py-2 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium">First Name</dt>
                            <dd className="mt-1 text-sm leading-6sm:col-span-2 sm:mt-0">{patientData ? patientData.first_name : null}</dd>
                          </div>
                          <div className="px-4 py-2 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium">Last Name</dt>
                            <dd className="mt-1 text-sm leading-6sm:col-span-2 sm:mt-0">{ patientData ? patientData.last_name : null}</dd>
                          </div>
                          <div className="px-4 py-2 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium">DOB</dt>
                            <dd className="mt-1 text-sm leading-6sm:col-span-2 sm:mt-0">{ patientData ? usDate(patientData.dob) : null}</dd>
                          </div>
                          <div className="px-4 py-2 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium">Phone</dt>
                            <dd className="mt-1 text-sm leading-6sm:col-span-2 sm:mt-0">{ patientData ? patientData.phone : null}</dd>
                          </div>
                          <div className="px-4 py-2 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium">Insurance</dt>
                            <dd className="mt-1 text-sm leading-6sm:col-span-2 sm:mt-0">{ patientData ? patientData.insurance_name : null}</dd>
                          </div>
                          <div className="px-4 py-2 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium">Insurance ID</dt>
                            <dd className="mt-1 text-sm leading-6sm:col-span-2 sm:mt-0">{ patientData ? patientData.insurance : null}</dd>
                          </div>
                        
                        </dl>
                      </div>
                    </div>

                    <div className="overflow-hidden bg-white border border-gray-200 rounded-md shadow  dark:bg-gray-800 dark:border-gray-700 sm:rounded-lg mb-5 md:mb-0">
                      <div className="px-4 py-3.5 sm:px-6 bg-indigo-500 dark:bg-gray-900">
                        <h3 className="text-base font-semibold leading-7 text-white flex items-center">
                          <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 me-2.5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z"/>
                          </svg>
                          <span>Reason for Visit</span>                        
                        </h3>
                      </div>
                      <div className="border-t border-gray-100 dark:border-gray-700">
                        <dl className="divide-y divide-gray-100 dark:divide-gray-700"> 
                          <div className="px-4 py-2 sm:grid sm:grid-cols-1 sm:gap-4 sm:px-6">
                            <dt className="text-sm ">Describe the primary reason for seeking medical attention. Be specific about symptoms, onset, and any relevant factors.
                              Keep it concise and focused, providing a clear overview of the patient's current health concern.</dt>
                          </div> 
                        </dl>
                      </div>
                    </div>
                    
                  </div>

                  <form onSubmit={reasonForVisitFormSubmit} className="bg-white border border-gray-200 rounded-md shadow relative dark:bg-gray-800 dark:border-gray-700 sm:rounded-lg md:col-span-2">
                    <div className={`h-96 text-center flex items-center justify-center ${loadDefaultData ? 'hidden' : 'block'}`}>                 
                      <div>                      
                        <h5 className='mb-[50px] text-slate-400 opacity-90'>Waiting as we retrieve your stored data</h5>
                        <PulseLoader color="#4f46e5" />
                      </div>
                    </div>
                    <div className={`px-4 mt-4 py-2 sm:p-8 ${loadDefaultData ? 'block' : 'hidden'}`}>
                      <div className="grid max-w-1x2 grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-12">

                        <div className="sm:col-span-full">
                          <div className='sm:flex items-center mt-2 sm:mt-0'>
                            <label className="block text-sm font-medium mb-1 sm:mb-0 w-32"> Visit Type </label>
                            <div className="w-full ">
                              <Select                           
                                required={loadDefaultData}
                                name="vt_id"
                                options={vtOptions}
                                onChange={vtHandleChange}
                                placeholder="Search.."
                                isSearchable={true} 
                                value={DBvtOption}
                                className="react-select-container"
                                classNamePrefix="react-select"
                              />
                            </div>
                          </div>
                        </div>
                       
                        <div className="sm:col-span-full">
                          <div className='lg:flex items-center'>
                            <label className="block text-sm font-medium mb-1 lg:mb-0 w-32"> Reason for Visit </label>
                            <div className="w-full">
                              <div className='relative'>
                                <div className="relative">
                                  <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                                    <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                                    </svg>
                                  </div>
                                  <input
                                    type="search"
                                    value={searchTerm}
                                    onChange={handleSearch}
                                    onClick={handleSearch}
                                    placeholder="Search..."
                                    className="block w-full rounded-md p-2 ps-10 text-sm outline-0 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6 dark:bg-white/5 dark:text-white dark:ring-white/10 dark:focus:ring-indigo-600"
                                  />
                                </div>
                                <div ref={resultsRef} className='absolute shadow-sm w-full rounded-lg z-10' >
                                  <ul className='max-h-64 overflow-x-auto bg-gray-100 dark:bg-gray-900 shadow-lg rounded scrollbar scrollbar-thumb-indigo-500 scrollbar-track-gray-200 dark:scrollbar-thumb-gray-300 dark:scrollbar-track-gray-600'>
                                    {searchResults.map((item, index) => {
                                      const existingRFVIds2 = localData('rfv_ids') || [];
                                      const isSelected = existingRFVIds2.includes(item.rfv_id);
                                      return (
                                        <li
                                          key={index}
                                          className={`p-1 px-2 cursor-pointer ${
                                            isSelected ? 'text-indigo-500 dark:text-lime-200 cursor-help ' : 'hover:text-gray-200 dark:text-gray-200 hover:bg-indigo-500'
                                          }`}
                                          onClick={() => rfvHandleChange(item.rfv_id)}
                                        >
                                          {item.rfv_name}
                                        </li>
                                      );
                                    })}
                                  </ul>
                                  {!showResults && searchTerm !== '' && (
                                    <p className='text-center p-10 backdrop-blur-sm bg-white/30 dark:bg-black/30 shadow-lg rounded'>No results found.</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="sm:col-span-full my-[-10px] lg:ml-28 ">   
                          <div id="accordion-open" data-accordion="open">
                            {Object.entries(reasonForVisitForm.rfvData)    
                              .sort(([, a], [, b]) => a.timestamp - b.timestamp)
                              .map(([rfv_id, staticData], index) => (
                                <div key={index} className='mb-2 bg-indigo-500  dark:bg-gray-700 shadow-md rounded-md w-full relative hover:bg-indigo-600 '>

                                    <h2 id={`accordion-open-heading-${index}`}>
                                        <button type="button"
                                            className={`w-full flex justify-between items-center px-4 py-2 lg:py-2.5 focus:outline-none rounded-t-md text-gray-50 dark:text-gray-100 `}
                                            data-accordion-target={`#accordion-open-body-${index}`}
                                            aria-expanded={openAccordions.includes(index)}
                                            aria-controls={`accordion-open-body-${index}`}
                                            onClick={() => handleAccordion(index)}
                                        >
                                            <span className='flex items-center'>
                                              {openAccordions.includes(index) ? (
                                                  <ChevronUpIcon className="h-4 w-4 text-semibold me-2 md:me-3" />
                                              ) : (
                                                  <ChevronDownIcon className="h-4 w-4 text-semibold me-2 md:me-3" />
                                              )}
                                              <span className="text-left">{ staticData.rfv_name}</span>
                                            </span>

                                            <span onClick={() => removeRfvRepeater(rfv_id)} className="cursor-pointer text-gray-50 hover:text-red-500  rounded-md p-4 mt-[-10px] mr-[-16px] mb-[-10px]" aria-hidden="true">
                                                <svg className="h-3 w-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
                                                </svg>
                                            </span>
                                        </button>
                                    </h2>
                                    <div id={`accordion-open-body-${index}`}
                                        className={`bg-gray-100 rounded-md dark:bg-gray-800 ${ openAccordions.includes(index) ? '' : 'hidden' }`}
                                        aria-labelledby={`accordion-open-heading-${index}`}
                                    >
                                        <div className="p-5 border border-b-0 border-gray-200 dark:border-gray-700 rounded-md">
                                          <div className="grid max-w-1x2 grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-12">

                                            <div className="sm:col-span-4 sm:col-start-1">
                                              <label className="block text-sm font-medium mb-1"> Duration </label>
                                              <div className="relative rounded-md shadow-sm">
                                                <input onChange={(e) => handleRfvRepeaterChange(rfv_id, 'duration', e.target.value)}
                                                  required={true}
                                                  defaultValue={staticData.duration}
                                                  type="number"
                                                  min={0}
                                                  name="duration" 
                                                  placeholder=""
                                                  autoComplete="duration"
                                                  className="px-2 py-1.5 ring-1 ring-inset ring-gray-300 block w-full rounded-md dark:ring-gray-600 placeholder:text-gray-400 outline-0 focus:ring-1 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                                  
                                                />
                                                <div className="absolute inset-y-0 right-0 flex items-center ">
                                                  <label className="sr-only"> Duration Type </label>
                                                  <select
                                                    onChange={(e) => handleRfvRepeaterChange(rfv_id, 'duration_type', e.target.value)}
                                                    defaultValue={staticData.duration_type}
                                                    name="duration_type"
                                                className="h-full rounded-e-md border-0 bg-white py-0 pl-2 pr-7 text-gray-500 sm:text-sm ring-1 ring-inset ring-gray-300 outline-0 
                                                dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:ring-gray-600 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500    
                                                "
                                                  >
                                                    <option value="Days">Days</option>
                                                    <option value="Weeks">Weeks</option>
                                                    <option value="Months">Months</option>
                                                    <option value="Years">Years</option>
                                                  </select>
                                                </div>
                                              </div>
                                            </div>

                                            {staticData.getDynamicCategoryOptions && (staticData.getDynamicCategoryOptions).map((category, index) => (
                                              <div className="sm:col-span-4" key={index}>{category}</div>
                                            ))}


                                            <div className="col-span-full">
                                              <label className="hidden text-sm font-medium mb-1"> Extra Notes </label>
                                              <textarea
                                                defaultValue={staticData.extra_notes}                                      
                                                onChange={(e) => handleRfvRepeaterChange(rfv_id, 'extra_notes', e.target.value)}
                                                placeholder='Extra Notes'
                                                name="extra_notes"
                                                rows={2}
                                                className="block p-1.5 outline-0 w-full text-sm text-gray-900 bg-white rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                              />
                                            </div>     
                                          </div>

                                        </div>
                                    </div>
                                </div>

                              ))}

                          </div>
                        </div>

                        

                        <div className="sm:col-span-full flex items-center">
                          <label className="block text-sm font-medium md:w-32"> Referral </label>
                          <div className="w-full">                          
                            <ul className="items-center w-full text-sm font-medium  bg-gray-100 border border-gray-200 rounded-md flex dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                              <li className="w-full border-b border-gray-200 sm:border-b-0 border-r dark:border-gray-600">
                                <div className="flex items-center ps-3">
                                  <input
                                    defaultChecked={isReferralYes}
                                    disabled={!DBvtOption}
                                    onChange={(e) => rfvInputHandleChange('referral', e.target.value)}
                                    id="referral-yes" type="radio" required value="yes" name="referral" className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 focus:ring-indigo-500 dark:focus:ring-indigo-600 dark:ring-offset-gray-700 dark:focus:ring-offset-gray-700 focus:ring-0 dark:bg-gray-600 dark:border-gray-500" />
                                  <label htmlFor="referral-yes" className="w-full py-2 ms-2 text-sm font-medium cursor-pointer dark:text-gray-300">Yes </label>
                                </div>
                              </li>
                            
                              <li className="w-full dark:border-gray-600">
                                <div className="flex items-center ps-3">
                                  <input
                                    defaultChecked={!isReferralYes}
                                    disabled={!DBvtOption}
                                    onChange={(e) => rfvInputHandleChange('referral', e.target.value)}
                                    id="referral-no" type="radio" required value="no" name="referral" className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 focus:ring-indigo-500 dark:focus:ring-indigo-600 dark:ring-offset-gray-700 dark:focus:ring-offset-gray-700 focus:ring-0 dark:bg-gray-600 dark:border-gray-500" />
                                    <label htmlFor="referral-no" className="w-full py-2 ms-2 text-sm font-medium cursor-pointer dark:text-gray-300">No</label>
                                </div>
                              </li>
                            </ul>
                          </div>
                        </div>

                      </div>
                    </div>

                    <div className="border-t border-gray-900/10 dark:border-gray-400/10">
                      <div className="flex items-center justify-between px-4 py-4 sm:px-8">
                        <Link to="/waiting-room" className=" bg-slate-400 hover:bg-slate-600 text-white  py-2 px-4 rounded inline-flex items-center">
                          <svg className="w-4 h-3 mr-2 text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10">
                            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 1 1 5l4 4"/>
                          </svg>
                          <span>Back</span>
                        </Link>

                        <div>
                      {/* <button onClick={saveToDb} className={`${isChange ? 'block' : 'hidden'} absolute right-8 mt-2 text-white bg-gradient-to-r from-purple-500 via-purple-600 to-purple-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-purple-300 dark:focus:ring-purple-800 font-medium rounded-md text-sm px-4 py-2 text-center z-10`}  type="button" >
                        SAVE
                      </button> */}
                        
                        <button type="button" onClick={saveToDb} className={`${isChange ? 'inline-block' : 'hidden'} bg-indigo-500 hover:bg-indigo-600 text-white py-2 px-4 rounded rounded-r-none mr-1`} >
                          <span>Save</span>
                        </button>
                          
                        <button type="submit" disabled={isSubmitting} className="bg-indigo-500 hover:bg-indigo-600 text-white py-2 px-4 rounded rounded-l-none inline-flex items-center">
                          <span>Next</span>
                          <svg className={`w-3 h-3 ml-3 text-white ${isSubmitting ? 'hidden' : ''}`} aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10">
                            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4"/>
                          </svg>             

                          <svg className={`w-4 h-4 ml-3 text-white animate-spin ${isSubmitting ? '' : 'hidden'}`} aria-hidden="true"  role="status" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#E5E7EB"/>
                            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor"/>
                          </svg>
                        </button>
                        </div>

                      </div>
                    </div>
                  </form>

                </div>   
                
              </div>

            </div>
          </div>

        </div> 
      </div>

      <Footer />
    </>
  );
};

export default ReasonForVisit;
